import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { Calendar, dateFnsLocalizer, Navigate } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { useMediaQuery } from "@mui/material";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import { useApi } from "../../utils/api";
import AddWorkoutDetails from "../AddWorkoutDetails";
import WorkoutCalendarView from "./WorkoutCalendarView";
import ManageProductsDialog from "./ManageProductsDialog";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Paper,
  IconButton,
  Box,
  Typography,
  Drawer,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";
import AssignProgramDialog from "./AssignProgramDialog";
import UploadDocumentModal from "./UploadDocumentModal";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import LoadingButton from "@mui/lab/LoadingButton";
import { useUserInfo } from "../../utils/user";
import "react-big-calendar/lib/css/react-big-calendar.css";
import enUS from "date-fns/locale/en-US";
const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const DragAndDropCalendar = withDragAndDrop(Calendar);

// Update theme to include button styles and primary color
const theme = createTheme({
  typography: {
    fontFamily: '"Roboto Mono", monospace',
  },
  palette: {
    primary: {
      main: "#ff0000", // Red color to match the rest of the app
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: '"Roboto Mono", monospace',
        },
      },
    },
  },
});

// Update calendar styles for two-week view
const calendarStyles = {
  height: "calc(100vh - 100px)",
  fontFamily: '"Roboto Mono", monospace !important',
  "& .rbc-month-view": {
    flexBasis: "auto !important",
    minHeight: "400px",
    height: "auto",
  },
  "& .rbc-month-row": {
    minHeight: "200px !important",
  },
  "& .rbc-header": {
    fontFamily: '"Roboto Mono", monospace',
    padding: "8px 0",
  },
  "& .rbc-button-link": {
    fontFamily: '"Roboto Mono", monospace',
  },
  "& .rbc-toolbar button": {
    fontFamily: '"Roboto Mono", monospace',
  },
  "& .rbc-row-segment": {
    padding: "0 2px",
  },
  // Hide rows beyond the first two weeks
  "& .rbc-month-row:nth-of-type(n+3)": {
    display: "none",
  },
  // Hide the last row of the month header (the 6th row)
  "& .rbc-month-header .rbc-row:last-child": {
    display: "none",
  },
};

// Custom toolbar with copy/move helper text on the right
const CalendarToolbar = ({ label, onNavigate, localizer: { messages } }) => (
  <div
    className="rbc-toolbar"
    style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}
  >
    <span className="rbc-btn-group">
      <button type="button" onClick={() => onNavigate(Navigate.PREVIOUS)}>
        {messages.previous}
      </button>
      <button type="button" onClick={() => onNavigate(Navigate.TODAY)}>
        {messages.today}
      </button>
      <button type="button" onClick={() => onNavigate(Navigate.NEXT)}>
        {messages.next}
      </button>
    </span>
    <span className="rbc-toolbar-label">{label}</span>
    <Typography
      variant="caption"
      sx={{
        marginLeft: "auto",
        color: "text.secondary",
      }}
    >
      <b>Copy:</b> Option (Alt) + click workout(s) <b>Move:</b> Shift + click
      workout(s)
    </Typography>
  </div>
);

// Custom day component for inline editing
const DayComponent = ({
  date,
  workouts = [],
  onSelectEvent,
  onSelectSlot,
  selectedWorkoutIds = new Set(),
  selectionMode = null,
}) => {
  return (
    <Box
      sx={{
        cursor: "pointer",
        p: 0.25,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelectSlot({
          start: date,
          end: date,
          isNew: true,
        });
      }}
    >
      {workouts.map((workout, i) => {
        const isSelected = selectedWorkoutIds.has(workout.id);
        const inCopyMode = selectionMode === "copy";
        const inMoveMode = selectionMode === "move";
        const selectedForCopy = inCopyMode && isSelected;
        const selectedForMove = inMoveMode && isSelected;
        const borderColor =
          selectedForCopy || selectedForMove ? "primary.main" : "transparent";
        const borderWidth = selectedForCopy || selectedForMove ? 2 : 0;
        return (
          <Paper
            key={workout.id || i}
            sx={{
              py: 1,
              px: 0.5,
              mb: 0,
              minHeight: "auto !important",
              cursor: "pointer",
              ...(selectedForCopy || selectedForMove
                ? {
                    bgcolor: "white",
                    color: "text.primary",
                    border: `${borderWidth}px solid`,
                    borderColor,
                  }
                : {
                    bgcolor: workout.completed
                      ? "success.light"
                      : "primary.main",
                    color: "white",
                    outline: "none",
                  }),
              transition:
                "background-color 0.15s, outline 0.15s, border-color 0.15s",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onSelectEvent(workout, e);
            }}
          >
            <Typography variant="body2" noWrap>
              {workout.name || workout.title}
            </Typography>
          </Paper>
        );
      })}
    </Box>
  );
};

const ClientWorkoutCalendar = ({
  clientId,
  onClientDeleted,
  setHeaderActions,
  onProductsSaved,
}) => {
  const { id: userId, stripeIsSetup } = useUserInfo();
  const isViewingSelf = userId === clientId;
  const [workouts, setWorkouts] = useState([]);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [client, setClient] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [repeatLoading, setRepeatLoading] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [productsDialogOpen, setProductsDialogOpen] = useState(false);
  const [days, setDays] = useState(30);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignPrograms, setAssignPrograms] = useState([]);
  const [assignTemplates, setAssignTemplates] = useState([]);
  const [assignProgramId, setAssignProgramId] = useState("");
  const [assignStartDate, setAssignStartDate] = useState(moment());
  const [assignDaySelections, setAssignDaySelections] = useState({});
  const [assignSaving, setAssignSaving] = useState(false);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedWorkoutIds, setSelectedWorkoutIds] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(null); // 'move' | 'copy'
  const mobile = useMediaQuery("(max-width: 600px)");
  const saveButtonId = "add-workout-save-button";
  const {
    getWorkoutsForUser,
    saveWorkoutDetails,
    addWorkoutForUser,
    getActivity,
    startConversation,
    getUser,
    deleteUser,
    getPrograms,
    getWorkoutTemplates,
  } = useApi();
  const auth = getAuth();

  // Refs to track the current clientId for each API call to prevent stale updates
  const currentWorkoutsClientIdRef = useRef(null);
  const currentActivityClientIdRef = useRef(null);
  const currentClientClientIdRef = useRef(null);

  // Track whether the Shift key is held for copy-on-drop behaviour
  const shiftKeyHeldRef = useRef(false);

  // Modify the dateRange initialization to check localStorage
  const [dateRange, setDateRange] = useState(() => {
    const savedDate = localStorage.getItem(`calendar-date-${clientId}`);
    if (savedDate) {
      const date = moment(savedDate);
      return {
        start: date.startOf("month"),
        end: date.endOf("month"),
      };
    }
    return {
      start: moment().startOf("month"),
      end: moment().endOf("month"),
    };
  });

  useEffect(() => {
    if (clientId) {
      // Reset refs when clientId changes to cancel previous requests
      currentWorkoutsClientIdRef.current = clientId;
      currentActivityClientIdRef.current = clientId;
      currentClientClientIdRef.current = clientId;

      fetchWorkouts();
      fetchClient();
    }
  }, [clientId, days]);

  const fetchClient = async () => {
    const requestClientId = clientId;
    try {
      const response = await getUser(requestClientId);
      // Only update state if this is still the current client
      if (currentClientClientIdRef.current === requestClientId) {
        setClient(response);
      }
    } catch (error) {
      // Only log error if this is still the current client
      if (currentClientClientIdRef.current === requestClientId) {
        console.error("Error fetching client:", error);
      }
    }
  };

  const fetchWorkouts = async (customDateRange = null) => {
    const requestClientId = clientId;
    try {
      const range = customDateRange || dateRange;
      const response = await getWorkoutsForUser(
        requestClientId,
        moment(range.start).subtract(1, "month").unix(),
        moment(range.end).add(1, "month").unix()
      );

      // Only update state if this is still the current client
      if (currentWorkoutsClientIdRef.current === requestClientId) {
        const formattedWorkouts = response.map((workout) => ({
          ...workout,
          title: workout.name,
          start: moment.unix(workout.date).startOf("day").toDate(),
          end: moment.unix(workout.date).endOf("day").toDate(),
          exercises: workout.exercises || [],
        }));

        setWorkouts(formattedWorkouts);
      }
    } catch (error) {
      // Only log error if this is still the current client
      if (currentWorkoutsClientIdRef.current === requestClientId) {
        console.error("Error fetching workouts:", error);
      }
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Shift") shiftKeyHeldRef.current = true;
    };
    const onKeyUp = (e) => {
      if (e.key === "Shift") shiftKeyHeldRef.current = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  const handleEventDrop = async ({ event, start }) => {
    const newDate = moment(start).startOf("day").unix();
    const currentDate = moment(event.start).startOf("day").unix();
    const isCopy = shiftKeyHeldRef.current;

    // Skip move when dropped on the same day (copy to same day is allowed)
    if (!isCopy && newDate === currentDate) return;

    if (isCopy) {
      // Copy: create a new workout on the target date
      const { id, title, start: _s, end: _e, ...rest } = event;
      try {
        await addWorkoutForUser(clientId, { ...rest, date: newDate });
        await fetchWorkouts();
      } catch (error) {
        console.error("Error copying workout:", error);
      }
    } else {
      // Move: optimistically update date, then persist
      setWorkouts((prev) =>
        prev.map((w) =>
          w.id === event.id
            ? {
                ...w,
                date: newDate,
                start: moment(start).startOf("day").toDate(),
                end: moment(start).endOf("day").toDate(),
              }
            : w
        )
      );
      try {
        await saveWorkoutDetails(event.id, { date: newDate });
        await fetchWorkouts();
      } catch (error) {
        console.error("Error moving workout:", error);
        await fetchWorkouts(); // revert optimistic update
      }
    }
  };

  const handleSelectEvent = (workout, e) => {
    const isShift = e?.shiftKey ?? false;
    const isAlt = e?.altKey ?? false;

    if (isShift || isAlt) {
      // Determine mode: alt (Option) = copy, shift-only = move
      const mode = isAlt ? "copy" : "move";

      // If the mode changed, reset selection to just this workout
      setSelectionMode((prevMode) => {
        if (prevMode && prevMode !== mode) {
          setSelectedWorkoutIds(new Set([workout.id]));
        } else {
          setSelectedWorkoutIds((prev) => {
            const next = new Set(prev);
            if (next.has(workout.id)) {
              next.delete(workout.id);
            } else {
              next.add(workout.id);
            }
            return next;
          });
        }
        return mode;
      });
      return;
    }

    // Regular click: clear any selection and open drawer
    if (selectedWorkoutIds.size > 0) {
      setSelectedWorkoutIds(new Set());
      setSelectionMode(null);
      return;
    }

    setHasUnsavedChanges(false);
    const formattedWorkout = {
      ...workout,
      id: workout.id,
      name: workout.title || workout.name,
      description: workout.description,
      exercises: workout.exercises || [],
      date: workout.date ? moment.unix(workout.date) : moment(workout.start),
    };
    setSelectedWorkout(formattedWorkout);
    setDrawerOpen(true);
  };

  const placeSelectedWorkouts = async (targetDate) => {
    const selected = workouts.filter((w) => selectedWorkoutIds.has(w.id));
    if (selected.length === 0) return;

    // Sort ascending by original date
    const sorted = [...selected].sort((a, b) => {
      const da = a.date ? moment.unix(a.date) : moment(a.start);
      const db = b.date ? moment.unix(b.date) : moment(b.start);
      return da.valueOf() - db.valueOf();
    });

    const earliestMoment = sorted[0].date
      ? moment.unix(sorted[0].date)
      : moment(sorted[0].start);
    const targetMoment = moment(targetDate).startOf("day");

    try {
      if (selectionMode === "copy") {
        await Promise.all(
          sorted.map((workout) => {
            const originalMoment = workout.date
              ? moment.unix(workout.date)
              : moment(workout.start);
            const offsetDays = originalMoment.diff(earliestMoment, "days");
            const newDate = targetMoment.clone().add(offsetDays, "days");
            return addWorkoutForUser(clientId, {
              name: workout.title || workout.name,
              description: workout.description,
              exercises: workout.exercises || [],
              date: newDate.unix(),
              completed: false,
            });
          })
        );
      } else {
        await Promise.all(
          sorted.map((workout) => {
            const originalMoment = workout.date
              ? moment.unix(workout.date)
              : moment(workout.start);
            const offsetDays = originalMoment.diff(earliestMoment, "days");
            const newDate = targetMoment.clone().add(offsetDays, "days");
            return saveWorkoutDetails(workout.id, {
              name: workout.title || workout.name,
              description: workout.description,
              exercises: workout.exercises || [],
              date: newDate.unix(),
              completed: workout.completed,
            });
          })
        );
      }
    } catch (error) {
      console.error("Error placing workouts:", error);
    }

    setSelectedWorkoutIds(new Set());
    setSelectionMode(null);
    await fetchWorkouts();
  };

  const handleSelectSlot = ({ start }) => {
    if (selectedWorkoutIds.size > 0) {
      placeSelectedWorkouts(start);
      return;
    }
    setHasUnsavedChanges(false);
    setSelectedWorkout({
      name: "",
      description: "",
      exercises: [],
      date: moment(start),
    });
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedWorkout(null);
    setHasUnsavedChanges(false);
  };

  const handleAttemptCloseDrawer = () => {
    if (hasUnsavedChanges) {
      setUnsavedDialogOpen(true);
      return;
    }
    handleCloseDrawer();
  };

  const handleConfirmSave = () => {
    const saveButton = document.getElementById(saveButtonId);
    if (saveButton) {
      saveButton.click();
    } else {
      // If the button cannot be found, fall back to closing to avoid trapping the user
      handleCloseDrawer();
    }
    setUnsavedDialogOpen(false);
  };

  const handleDiscardChanges = () => {
    setUnsavedDialogOpen(false);
    handleCloseDrawer();
  };

  const handleCancelClose = () => {
    setUnsavedDialogOpen(false);
  };

  const handleSaveWorkout = async (workoutData) => {
    try {
      console.log("Saving workout:", workoutData); // Debug log
      if (selectedWorkout?.id) {
        await saveWorkoutDetails(selectedWorkout.id, workoutData);
      } else {
        await addWorkoutForUser(clientId, workoutData);
      }
      await fetchWorkouts();
      handleCloseDrawer();
    } catch (error) {
      console.error("Error saving workout:", error);
    }
  };

  const handleNavigate = (date) => {
    const start = moment(date).startOf("month");
    const end = moment(date).endOf("month");

    // Save the current date to localStorage
    localStorage.setItem(`calendar-date-${clientId}`, moment(date).format());

    const newDateRange = {
      start,
      end,
    };

    setDateRange(newDateRange);

    // Fetch workouts for the new date range
    fetchWorkouts(newDateRange);
  };

  // Add cleanup effect to remove localStorage item when component unmounts
  useEffect(() => {
    return () => {
      localStorage.removeItem(`calendar-date-${clientId}`);
    };
  }, [clientId]);

  // Clear selection on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && selectedWorkoutIds.size > 0) {
        setSelectedWorkoutIds(new Set());
        setSelectionMode(null);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedWorkoutIds]);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleRepeatLastWeek = async () => {
    try {
      setRepeatLoading(true);
      const response = await getWorkoutsForUser(
        clientId,
        moment().subtract(12, "month").unix(),
        moment().add(12, "month").unix()
      );

      if (!response.length) {
        alert("No workouts found to repeat");
        return;
      }

      const formattedWorkouts = response.map((workout) => ({
        ...workout,
        start: moment.unix(workout.date).startOf("day").toDate(),
      }));

      const sortedWorkouts = formattedWorkouts.sort(
        (a, b) => moment(b.start).valueOf() - moment(a.start).valueOf()
      );

      const mostRecentDate = moment(sortedWorkouts[0].start);
      const mostRecentWeekWorkouts = sortedWorkouts.filter((workout) =>
        moment(workout.start).isSame(mostRecentDate, "week")
      );

      const newWorkouts = mostRecentWeekWorkouts.map((workout) => ({
        ...workout,
        date: moment(workout.start).add(7, "days").unix(),
        exercises: workout.exercises.map((exercise) => {
          const newExercise = { ...exercise };
          // Clear values based on set type
          newExercise.sets = exercise.sets.map((set) => {
            const newSet = { ...set };
            // Clear weight for RPE sets
            if (set.type === "RPE") {
              delete newSet.weight;
            }
            // Clear RPE for weight-based sets
            if (set.type === "WEIGHT") {
              delete newSet.rpe;
            }
            return newSet;
          });
          return newExercise;
        }),
      }));

      await Promise.all(
        newWorkouts.map((workout) => addWorkoutForUser(clientId, workout))
      );

      handleCloseMenu();
      fetchWorkouts();
    } catch (error) {
      console.error("Error repeating last week's workouts:", error);
      alert("Failed to repeat last week's workouts. Please try again.");
    } finally {
      setRepeatLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    // Prevent coaches from deleting themselves
    if (isViewingSelf) {
      alert("You cannot delete your own account.");
      return;
    }
    try {
      setDeleteLoading(true);
      await deleteUser(clientId);
      handleCloseMenu();
      if (onClientDeleted) {
        onClientDeleted();
      }
    } catch (error) {
      console.error("Failed to delete client:", error);
      alert("Failed to remove client. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      setResetPasswordLoading(true);
      if (client?.email) {
        await sendPasswordResetEmail(auth, client.email);
        handleCloseMenu();
      }
    } catch (error) {
      console.error("Failed to send password reset email:", error);
      alert("Failed to send password reset email. Please try again.");
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleCloseConfirmDelete = () => {
    setConfirmDeleteOpen(false);
  };

  const handleOpenProductsDialog = () => {
    handleCloseMenu();
    setProductsDialogOpen(true);
  };

  const handleCloseProductsDialog = () => {
    setProductsDialogOpen(false);
  };

  const handleOpenDocumentsDialog = () => {
    handleCloseMenu();
    setDocumentsDialogOpen(true);
  };

  const handleCloseDocumentsDialog = () => {
    setDocumentsDialogOpen(false);
  };

  const handleOpenAssignDialog = async () => {
    setAssignDialogOpen(true);
    if (!assignPrograms.length) {
      const progs = (await getPrograms()) || [];
      setAssignPrograms(progs);
    }
    if (!assignTemplates.length) {
      const temps = (await getWorkoutTemplates()) || [];
      setAssignTemplates(temps);
    }
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setAssignProgramId("");
    setAssignDaySelections({});
    setAssignSaving(false);
    setAssignStartDate(moment());
  };

  const handleProgramChange = (programId) => {
    setAssignProgramId(programId);
    const program = assignPrograms.find((p) => p.id === programId);
    if (program) {
      const selections = {};
      (program.weeks || []).forEach((week) =>
        week?.forEach((tid) => {
          selections[tid] = selections[tid] ?? 1; // default Monday
        })
      );
      setAssignDaySelections(selections);
    } else {
      setAssignDaySelections({});
    }
  };

  const handleDayChange = (templateId, day) => {
    setAssignDaySelections((prev) => ({ ...prev, [templateId]: day }));
  };

  const handleAssignProgram = async () => {
    if (!assignProgramId || !assignStartDate) return;
    const program = assignPrograms.find((p) => p.id === assignProgramId);
    if (!program) return;
    setAssignSaving(true);
    const base = moment(assignStartDate).startOf("day");
    try {
      const allTemplatesMap = new Map(assignTemplates.map((t) => [t.id, t]));
      for (let weekIdx = 0; weekIdx < (program.weeks || []).length; weekIdx++) {
        const weekTemplates = program.weeks[weekIdx] || [];
        for (let i = 0; i < weekTemplates.length; i++) {
          const tid = weekTemplates[i];
          const tmpl = allTemplatesMap.get(tid);
          if (!tmpl) continue;
          const day = assignDaySelections[tid] ?? 1; // Monday default
          const targetDate = base.clone().add(weekIdx, "weeks").weekday(day);
          const payload = {
            name: tmpl.name,
            description: tmpl.description,
            exercises: tmpl.exercises,
            date: targetDate.unix(),
            completed: false,
          };
          await addWorkoutForUser(clientId, payload);
        }
      }
      handleCloseAssignDialog();
      fetchWorkouts();
    } catch (err) {
      console.error("Failed to assign program:", err);
      alert("Failed to assign program. Please try again.");
    } finally {
      setAssignSaving(false);
    }
  };

  const menuItems = (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleCloseMenu}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        sx: {
          minWidth: "200px",
          maxWidth: "300px",
        },
      }}
    >
      <LoadingButton
        onClick={handleRepeatLastWeek}
        loading={repeatLoading}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          textTransform: "none",
          color: "inherit",
          px: 2,
          py: 1,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        Repeat Last Week
      </LoadingButton>
      <LoadingButton
        onClick={() => {
          handleCloseMenu();
          handleOpenAssignDialog();
        }}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          textTransform: "none",
          color: "inherit",
          px: 2,
          py: 1,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        Assign Program
      </LoadingButton>
      {stripeIsSetup && (
        <LoadingButton
          onClick={handleOpenProductsDialog}
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            textTransform: "none",
            color: "inherit",
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          Manage Products
        </LoadingButton>
      )}
      <LoadingButton
        onClick={handleOpenDocumentsDialog}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          textTransform: "none",
          color: "inherit",
          px: 2,
          py: 1,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        Upload Documents
      </LoadingButton>
      {!isViewingSelf && (
        <LoadingButton
          onClick={handleResetPassword}
          loading={resetPasswordLoading}
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            textTransform: "none",
            color: "inherit",
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          Reset Password
        </LoadingButton>
      )}
      {!isViewingSelf && (
        <LoadingButton
          onClick={handleDeleteClient}
          loading={deleteLoading}
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            textTransform: "none",
            color: "error.main",
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          Remove Client
        </LoadingButton>
      )}
    </Menu>
  );

  useEffect(() => {
    if (setHeaderActions) {
      setHeaderActions(
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={handleOpenMenu}>
            <MoreVertIcon />
          </IconButton>
          {menuItems}
        </Box>
      );
    }
    return () => setHeaderActions && setHeaderActions(null);
  }, [
    setHeaderActions,
    anchorEl,
    repeatLoading,
    resetPasswordLoading,
    deleteLoading,
    productsDialogOpen,
    confirmDeleteOpen,
    documentsDialogOpen,
  ]);

  return (
    <ThemeProvider theme={theme}>
      <div className="h-screen p-4">
        <Dialog
          open={confirmDeleteOpen}
          onClose={handleCloseConfirmDelete}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Remove Client</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to remove this client? This action cannot be
              undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmDelete}>Cancel</Button>
            <Button onClick={handleDeleteClient} color="error" autoFocus>
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={unsavedDialogOpen}
          onClose={handleCancelClose}
          aria-labelledby="unsaved-changes-title"
        >
          <DialogTitle id="unsaved-changes-title">Unsaved changes</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You have unsaved changes. Do you want to save before closing?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmSave} color="primary">
              Save
            </Button>
            <Button onClick={handleDiscardChanges} color="error">
              Close without saving
            </Button>
            <Button onClick={handleCancelClose}>Cancel</Button>
          </DialogActions>
        </Dialog>

        <AssignProgramDialog
          open={assignDialogOpen}
          onClose={handleCloseAssignDialog}
          programs={assignPrograms}
          templates={assignTemplates}
          selectedProgramId={assignProgramId}
          onProgramChange={handleProgramChange}
          startDate={assignStartDate}
          onStartDateChange={setAssignStartDate}
          daySelections={assignDaySelections}
          onDayChange={handleDayChange}
          onAssign={handleAssignProgram}
          saving={assignSaving}
        />

        <ManageProductsDialog
          open={productsDialogOpen}
          onClose={handleCloseProductsDialog}
          onSaved={onProductsSaved}
          client={client}
        />

        <UploadDocumentModal
          open={documentsDialogOpen}
          onClose={handleCloseDocumentsDialog}
          clientId={clientId}
          clientName={client?.name}
        />

        <>
          {selectedWorkoutIds.size > 0 && (
            <Box
              sx={{
                mb: 1,
                px: 2,
                py: 1,
                borderRadius: 1,
                bgcolor: "primary.main",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography variant="body2">
                {selectedWorkoutIds.size} workout
                {selectedWorkoutIds.size !== 1 ? "s" : ""} selected for{" "}
                <b>{selectionMode === "copy" ? "copy" : "move"}</b>. Click a
                date to place them.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                sx={{ color: "white", borderColor: "white", flexShrink: 0 }}
                onClick={() => {
                  setSelectedWorkoutIds(new Set());
                  setSelectionMode(null);
                }}
              >
                Cancel (Esc)
              </Button>
            </Box>
          )}
          <DragAndDropCalendar
            localizer={localizer}
            events={workouts}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            onNavigate={handleNavigate}
            onEventDrop={handleEventDrop}
            draggableAccessor={() => true}
            resizableAccessor={() => false}
            selectable
            style={calendarStyles}
            views={["month"]}
            defaultView="month"
            date={dateRange.start.toDate()}
            components={{
              toolbar: CalendarToolbar,
              event: ({ event }) => (
                <div style={{ position: "relative" }}>
                  <DayComponent
                    date={event.start}
                    workouts={[event]}
                    onSelectEvent={handleSelectEvent}
                    onSelectSlot={handleSelectSlot}
                    selectedWorkoutIds={selectedWorkoutIds}
                    selectionMode={selectionMode}
                  />
                </div>
              ),
            }}
          />

          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={handleAttemptCloseDrawer}
            PaperProps={{
              sx: {
                width: mobile ? "100vw" : "60vw",
                minWidth: mobile ? "100vw" : "800px",
              },
            }}
          >
            <ThemeProvider theme={theme}>
              <Box sx={{ display: "flex", height: "100%" }}>
                <AddWorkoutDetails
                  open={drawerOpen}
                  handleClose={handleCloseDrawer}
                  userId={clientId}
                  workout={selectedWorkout}
                  onSave={handleSaveWorkout}
                  onWorkoutCopied={fetchWorkouts}
                  onWorkoutDeleted={fetchWorkouts}
                  inline={true}
                  onDirtyChange={setHasUnsavedChanges}
                  submitButtonId={saveButtonId}
                />
              </Box>
            </ThemeProvider>
          </Drawer>
        </>
      </div>
    </ThemeProvider>
  );
};

export default ClientWorkoutCalendar;
